import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Box,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  List,
  ListItemText,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

import { useNavigate } from "react-router-dom";
import { apiClient } from "@/services/api";

import CreateMonitoringTargetDialog from '../projects/CreateMonitoringTargetDialog.tsx';
import { showSuccessMessage, showErrorMessage } from '@/components/common/GlobalSnackbar.tsx';

// TypeScript 类型定义
interface User {
  id: number;
  username: string;
  role: string;
  org_id: number;
}

interface Project {
  id: number;
  project_name: string;
  project_code: string;
  description?: string;
  user_id: number;
  org_id: number;
  report_template_id?: number;
  industry_type?: string;
  client_level?: string;
  evaluation_type?: string;
  status: "new" | "archived_mentor";
  contract_year?: number;
  dify_dataset_id?: string;
  project_type: string;
  created_at: string;
  updated_at: string;
  report_generation_status?: string;
  report_generation_progress?: number;
  report_generation_error?: string;
  last_generated_at?: string;
  username?: string;
  display_name?: string;  // 创建者显示名称
  organization_name?: string;
  user?: {
    display_name?: string;
    username?: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`monitoring-tabpanel-${index}`}
      aria-labelledby={`monitoring-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function MonitoringListItem({
  project,
  onDelete,
  onEdit,
  onNavigate,
  loadingStates,
  getUsernameById,
  formatToGMT8,
  currentUser,
}: {
  project: Project;
  onDelete: (project: Project) => void;
  onEdit: (project: Project) => void;
  onNavigate: (projectId: number) => void;
  loadingStates: any;
  getUsernameById: (userId: number) => string;
  formatToGMT8: (dateStr: string | null) => string;
  currentUser: User | null;
}) {
  // 检查项目是否正在自动处理中
  const isProcessing = project.report_generation_status === "generating";
  // 检查项目是否已完成报告生成
  const isCompleted = project.report_generation_status === "completed";

  // 解析创建者显示名称
  const resolveDisplayName = (project: Project): string => {
    if (project.display_name) return project.display_name;
    if (project.username) return project.username;
    if (project.user?.display_name) return project.user.display_name;
    if (project.user?.username) return project.user.username;
    return `用户${project.user_id}`;
  };

  return (
    <React.Fragment>
      <div
        onClick={() => {
          onNavigate(project.id);
        }}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          padding: "12px 0",
          cursor: "pointer",
        }}
      >
        <ListItemText
          primary={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {project.project_name}
              </Typography>
              {isProcessing && (
                <Tooltip title="正在生成报告...">
                  <CircularProgress size={16} />
                </Tooltip>
              )}
              {isCompleted && (
                <Tooltip title="报告已生成">
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "success.main",
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          }
          secondary={
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                项目编码: {project.project_code}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                创建者: {resolveDisplayName(project)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                创建时间: {formatToGMT8(project.created_at)}
              </Typography>
            </Box>
          }
        />
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Tooltip title="编辑">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(project);
              }}
              disabled={loadingStates.editing && loadingStates.projectId === project.id}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {(currentUser?.role === "super_admin" || currentUser?.role === "zhaoyuan_admin") && (
            <Tooltip title="删除">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(project);
                }}
                disabled={loadingStates.deleting && loadingStates.projectId === project.id}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </div>
    </React.Fragment>
  );
}

interface MonitoringFormProps {
  // 可以添加props用于接收外部数据或回调
}

const MonitoringForm: React.FC<MonitoringFormProps> = () => {
  const navigate = useNavigate();
  
  // 状态管理
  const [projectTargets, setProjectTargets] = useState<Project[]>([]); // 具体目标监控
  const [departmentProjects, setDepartmentProjects] = useState<Project[]>([]); // 部门监控项目
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  // 页签状态
  const [tabValue, setTabValue] = useState(0); // 0: 具体目标监控, 1: 整体部门监控
  
  // 过滤状态
  const [projectFilterText, setProjectFilterText] = useState("");
  
  // 对话框状态（具体目标监控）
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  
  // 对话框状态（部门监控）
  const [createDeptDialogOpen, setCreateDeptDialogOpen] = useState(false);
  const [editDeptDialogOpen, setEditDeptDialogOpen] = useState(false);
  const [deptProjectToEdit, setDeptProjectToEdit] = useState<Project | null>(null);
  const [deleteDeptDialogOpen, setDeleteDeptDialogOpen] = useState(false);
  const [deptProjectToDelete, setDeptProjectToDelete] = useState<Project | null>(null);
  
  // 加载状态
  const [loadingAction, setLoadingAction] = useState({
    editing: false,
    deleting: false,
    projectId: null as number | null,
  });
  
  // 获取当前用户
  useEffect(() => {
    const userStr = localStorage.getItem("currentUser");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
      } catch (e) {
      }
    }
  }, []);

  // 获取用户名（仅根据当前列表）
  const getUsernameById = useCallback((userId: number): string => {
    const project = projectTargets.find(p => p.user_id === userId);
    return project?.username || project?.display_name || `用户${userId}`;
  }, [projectTargets]);

  // 格式化日期（后端返回的时间已加8小时，但时区标记是+00:00，需要修正）
  const formatToGMT8 = useCallback((dateStr: string | null): string => {
    if (!dateStr) return "未知";
    try {
      // 如果后端返回的时间时区标记是+00:00，但实际已经是北京时间，需要替换为+08:00
      let normalized = dateStr.replace(" ", "T");
      // 将 +00:00 替换为 +08:00，因为后端返回的时间已经是北京时间
      if (normalized.includes("+00:00")) {
        normalized = normalized.replace("+00:00", "+08:00");
      }
      const date = new Date(normalized);
      if (isNaN(date.getTime())) return dateStr;
      // 直接格式化，不进行时区转换（因为时间已经是正确的北京时间）
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      return `${year}/${month}/${day} ${hour}:${minute}`;
    } catch {
      return dateStr;
    }
  }, []);

  // 获取具体目标监控列表（使用独立的 project_type: monitoring_target）
  const fetchProjectTargets = useCallback(async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get('/projects/', {
        page: 1,
        page_size: 100,
        project_type: "monitoring_target",
      });
      
      
      if (response.success && response.data) {
        setProjectTargets(response.data);
        setError(null);
      } else {
        setError(response.message || "获取具体目标监控列表失败");
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || "获取具体目标监控列表失败");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // 获取部门监控项目列表（使用 project_type: department_monitoring）
  const fetchDepartmentProjects = useCallback(async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get('/projects/', {
        page: 1,
        page_size: 100,
        project_type: "department_monitoring",
      });
      
      
      if (response.success && response.data) {
        setDepartmentProjects(response.data);
        setError(null);
      } else {
        setError(response.message || "获取部门监控项目列表失败");
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || "获取部门监控项目列表失败");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // 初始加载
  useEffect(() => {
    fetchProjectTargets();
    fetchDepartmentProjects();
  }, [fetchProjectTargets, fetchDepartmentProjects]);

  // 处理项目点击（跳转到监控指标管理页面）
  const handleNavigate = useCallback((projectId: number) => {
    // 直接跳转到监控指标管理页面
    navigate(`/projects/${projectId}/monitoring-indicators`);
  }, [navigate]);

  // 处理编辑项目（打开编辑对话框而不是跳转）
  const handleEditProject = useCallback((project: Project) => {
    setProjectToEdit(project);
    setEditProjectDialogOpen(true);
  }, []);

  // 处理删除项目
  const handleDeleteProject = useCallback((project: Project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  }, []);

  // 确认删除项目
  const confirmDeleteProject = useCallback(async () => {
    if (!projectToDelete) return;
    
    setLoadingAction({ editing: false, deleting: true, projectId: projectToDelete.id });
    
    try {
      await apiClient.delete(`/projects/${projectToDelete.id}`);
      showSuccessMessage("项目删除成功");
      
      // 删除后刷新列表
      fetchProjectTargets();
    } catch (err: any) {
      showErrorMessage(err.response?.data?.detail || "删除项目失败");
    } finally {
      setLoadingAction({ editing: false, deleting: false, projectId: null });
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  }, [projectToDelete, fetchProjectTargets]);

  // 处理创建具体目标监控
  const handleCreateProject = useCallback(() => {
    setCreateProjectDialogOpen(true);
  }, []);

  // 处理具体目标监控创建/编辑成功
  const handleProjectCreated = useCallback(() => {
    setCreateProjectDialogOpen(false);
    setEditProjectDialogOpen(false);
    setProjectToEdit(null);
    fetchProjectTargets();
  }, [fetchProjectTargets]);

  // 部门监控项目相关处理函数
  const handleDeptNavigate = useCallback((projectId: number) => {
    // 直接跳转到部门监控指标管理页面
    navigate(`/projects/${projectId}/department-monitoring`);
  }, [navigate]);

  const handleDeptEdit = useCallback((project: Project) => {
    setDeptProjectToEdit(project);
    setEditDeptDialogOpen(true);
  }, []);

  const handleDeptDelete = useCallback((project: Project) => {
    setDeptProjectToDelete(project);
    setDeleteDeptDialogOpen(true);
  }, []);

  const confirmDeptDelete = useCallback(async () => {
    if (!deptProjectToDelete) return;
    
    setLoadingAction({ editing: false, deleting: true, projectId: deptProjectToDelete.id });
    
    try {
      await apiClient.delete(`/projects/${deptProjectToDelete.id}`);
      showSuccessMessage("部门监控项目删除成功");
      fetchDepartmentProjects();
    } catch (err: any) {
      showErrorMessage(err.response?.data?.detail || "删除部门监控项目失败");
    } finally {
      setLoadingAction({ editing: false, deleting: false, projectId: null });
      setDeleteDeptDialogOpen(false);
      setDeptProjectToDelete(null);
    }
  }, [deptProjectToDelete, fetchDepartmentProjects]);

  const handleDeptProjectCreated = useCallback(() => {
    setCreateDeptDialogOpen(false);
    setEditDeptDialogOpen(false);
    setDeptProjectToEdit(null);
    fetchDepartmentProjects();
  }, [fetchDepartmentProjects]);

  // 计算统计数据（目前只统计具体目标监控）
  const projectStats = useMemo(() => {
    const totalInProgress = projectTargets.filter(p => p.status === "new").length;
    const totalArchived = projectTargets.filter(p => p.status === "archived_mentor").length;
    const totalGenerating = projectTargets.filter(p => p.report_generation_status === "generating").length;
    
    return {
      totalInProgress,
      totalArchived,
      totalGenerating,
      total: projectTargets.length
    };
  }, [projectTargets]);

  // 过滤项目
  const filteredProjectTargets = useMemo(() => {
    let filtered = projectTargets;
    
    if (projectFilterText) {
      filtered = filtered.filter(project =>
        project.project_name.toLowerCase().includes(projectFilterText.toLowerCase()) ||
        project.project_code.toLowerCase().includes(projectFilterText.toLowerCase())
      );
    }
    
    return filtered;
  }, [projectTargets, projectFilterText]);

  // 按状态分组项目
  const inProgressProjectTargets = useMemo(() => 
    filteredProjectTargets.filter(p => p.status === "new"), 
    [filteredProjectTargets]
  );

  // 过滤部门监控项目
  const filteredDepartmentProjects = useMemo(() => {
    let filtered = departmentProjects;
    
    if (projectFilterText) {
      filtered = filtered.filter(project =>
        project.project_name.toLowerCase().includes(projectFilterText.toLowerCase()) ||
        project.project_code.toLowerCase().includes(projectFilterText.toLowerCase())
      );
    }
    
    return filtered;
  }, [departmentProjects, projectFilterText]);
  
  if (!currentUser) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const commonListProps = {
    onDelete: handleDeleteProject,
    onEdit: handleEditProject,
    onNavigate: handleNavigate,
    loadingStates: loadingAction,
    getUsernameById,
    formatToGMT8,
    currentUser,
  };

  return (
    <Paper elevation={0} sx={{ p: 2, mt: 1, background: 'transparent' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} aria-label="monitoring tabs">
          <Tab label={`项目监控 (${projectStats.total})`} id="monitoring-tab-0" />
          <Tab label={`部门监控`} id="monitoring-tab-1" />
        </Tabs>
      </Box>

      {/* 具体目标监控页签 */}
      <TabPanel value={tabValue} index={0}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="搜索具体目标监控..."
              value={projectFilterText}
              onChange={(e) => setProjectFilterText(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleCreateProject}
          >
            创建新项目
          </Button>
        </Box>
        <List>
          {inProgressProjectTargets.length > 0 ? (
            inProgressProjectTargets.map((p) => (
              <MonitoringListItem key={p.id} project={p} {...commonListProps} />
            ))
          ) : (
            <Alert severity="info">您当前没有正在监控的具体目标。</Alert>
          )}
        </List>
      </TabPanel>

      {/* 整体部门监控页签 */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="搜索项目名称或编码..."
            value={projectFilterText}
            onChange={(e) => setProjectFilterText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setCreateDeptDialogOpen(true)}
          >
            创建部门监控项目
          </Button>
        </Box>
        <List>
          {filteredDepartmentProjects.length > 0 ? (
            filteredDepartmentProjects.map((p) => (
              <MonitoringListItem 
                key={p.id} 
                project={p} 
                onNavigate={handleDeptNavigate}
                onEdit={handleDeptEdit}
                onDelete={handleDeptDelete}
                loadingStates={loadingAction}
                getUsernameById={getUsernameById}
                formatToGMT8={formatToGMT8}
                currentUser={currentUser}
              />
            ))
          ) : (
            <Alert severity="info">暂无部门监控项目，点击右上角创建新项目。</Alert>
          )}
        </List>
      </TabPanel>

      {/* 创建具体目标监控对话框 */}
      <CreateMonitoringTargetDialog
        open={createProjectDialogOpen}
        onClose={() => setCreateProjectDialogOpen(false)}
        onProjectCreated={handleProjectCreated}
      />

      {/* 编辑具体目标监控对话框 */}
      <CreateMonitoringTargetDialog
        open={editProjectDialogOpen}
        onClose={() => { setEditProjectDialogOpen(false); setProjectToEdit(null); }}
        onProjectCreated={handleProjectCreated}
        editMode
        projectDataToEdit={projectToEdit}
      />

      {/* 删除确认对话框 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除项目 "{projectToDelete?.project_name}" 吗？此操作不可撤销。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={confirmDeleteProject} color="error" variant="contained">
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 创建部门监控项目对话框 */}
      <CreateMonitoringTargetDialog
        open={createDeptDialogOpen}
        onClose={() => setCreateDeptDialogOpen(false)}
        onProjectCreated={handleDeptProjectCreated}
        projectType="department_monitoring"
      />

      {/* 编辑部门监控项目对话框 */}
      <CreateMonitoringTargetDialog
        open={editDeptDialogOpen}
        onClose={() => { setEditDeptDialogOpen(false); setDeptProjectToEdit(null); }}
        onProjectCreated={handleDeptProjectCreated}
        editMode
        projectDataToEdit={deptProjectToEdit}
        projectType="department_monitoring"
      />

      {/* 删除部门监控项目确认对话框 */}
      <Dialog open={deleteDeptDialogOpen} onClose={() => setDeleteDeptDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除部门监控项目 "{deptProjectToDelete?.project_name}" 吗？此操作不可撤销。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDeptDialogOpen(false)}>取消</Button>
          <Button onClick={confirmDeptDelete} color="error" variant="contained">
            删除
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MonitoringForm;

